export type UnitSystem = 'metric' | 'imperial';

export interface PalletDimensions {
  length: number; // Stored internally as cm
  width: number;  // Stored internally as cm
  height: number; // Stored internally as cm
  weight: number; // Stored internally as kg
}

export interface PalletConfig extends PalletDimensions {
  id: string;
  quantity: number;
  color: string;
  name: string;
  stackable: boolean;
  maxStackWeight: number; // kg
}

export interface PlacedPallet {
  id: string;
  configId: string; // Ref to original config
  name: string; // Display name
  x: number; // meters relative to truck center
  y: number; // meters relative to floor
  z: number; // meters relative to front
  width: number; // meters
  height: number; // meters
  length: number; // meters
  color: string;
  weight: number;
  rotation: number; // radians (y-axis)
  
  // Stacking Props
  stackable: boolean;
  maxStackWeight: number;
  stackedOn: string | null; // ID of the pallet below
}

export interface TruckSpecs {
  id: string;
  name: string;
  region: 'EU' | 'US' | 'Global' | 'Custom';
  width: number; // meters (Internal)
  length: number; // meters (Internal)
  height: number; // meters (Internal)
  maxWeight: number; // kg
}

export interface LoadResult {
  placedPallets: PlacedPallet[];
  totalWeight: number;
  usedLength: number; // meters
  percentFull: number;
  overweight: boolean;
}

export interface SavedLoad {
  id: string;
  name: string;
  date: number; // Timestamp
  truckId: string;
  placedPallets: PlacedPallet[]; // Changed to store exact positions including stacks
}

export const TRUCK_PRESETS: TruckSpecs[] = [
  // EU Trucks
  {
    id: 'eu-std',
    name: 'EU Tautliner Standard',
    region: 'EU',
    width: 2.48,
    length: 13.6,
    height: 2.70,
    maxWeight: 24000
  },
  {
    id: 'eu-mega',
    name: 'EU Mega Trailer',
    region: 'EU',
    width: 2.48,
    length: 13.6,
    height: 3.00,
    maxWeight: 24000
  },
  {
    id: 'eu-box',
    name: 'EU Box Trailer (Frigo)',
    region: 'EU',
    width: 2.46,
    length: 13.4,
    height: 2.65,
    maxWeight: 22000
  },
  {
    id: 'eu-road-train',
    name: 'EU Road Train (Volumen)',
    region: 'EU',
    width: 2.48,
    length: 15.65, // Combined length approx
    height: 3.00,
    maxWeight: 24000
  },

  // US Trucks
  {
    id: 'us-53-dry',
    name: 'US Dry Van 53\'',
    region: 'US',
    width: 2.59, // ~102 inches
    length: 16.15, // ~53 feet
    height: 2.80, // ~110 inches
    maxWeight: 20400 // ~45,000 lbs payload
  },
  {
    id: 'us-53-reefer',
    name: 'US Reefer 53\'',
    region: 'US',
    width: 2.54, // Slightly narrower due to insulation
    length: 16.00, 
    height: 2.75, 
    maxWeight: 19500 
  },
  {
    id: 'us-48-flatbed',
    name: 'US Flatbed 48\'',
    region: 'US',
    width: 2.59, 
    length: 14.63, 
    height: 2.60, // Load height limit usually
    maxWeight: 21700 
  },

  // Global / Containers
  {
    id: 'cont-20',
    name: 'Container 20\' GP',
    region: 'Global',
    width: 2.35,
    length: 5.90,
    height: 2.39,
    maxWeight: 21000
  },
  {
    id: 'cont-40hc',
    name: 'Container 40\' HC',
    region: 'Global',
    width: 2.35,
    length: 12.03,
    height: 2.69,
    maxWeight: 26500
  },
  
  // Custom placeholder
  {
    id: 'custom',
    name: 'Custom / Manual',
    region: 'Custom',
    width: 2.50,
    length: 13.6,
    height: 2.70,
    maxWeight: 24000
  }
];